/** Owner picker beside the map's search bar. Empty selection means every owner. */
import { Check, ChevronDown, Search, UserRound } from 'lucide-react';
import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import type { OwnerSelection } from '@/features/locations/utils/MapLayersService';
import { useSession } from '@/features/auth/context/SessionContext';
import { useOwnersQuery } from '@/features/map/services/owners.api';
import { THIN_SCROLLBAR } from '@/libs/utils/thinScrollbar';
import { MAP_PILL_NARROWED_CLASS } from './mapBarGeometry';
import { cn } from '@/libs/utils/cn';

interface MapOwnerFilterProps {
  selection: OwnerSelection;
  onToggle: (owner: string) => void;
  onClear: () => void;
}

const AVATAR_COLOURS = ['bg-primary-700', 'bg-zone-mine', 'bg-brand-800'] as const;

const MAX_TRIGGER_AVATARS = 2;

const AVATAR_SHAPE =
  'flex size-6 flex-none items-center justify-center rounded-full text-[10px] font-bold';

export function MapOwnerFilter({ selection, onToggle, onClear }: MapOwnerFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data } = useOwnersQuery();
  const { user } = useSession();
  const me = user?.email.toLowerCase();

  // Me, then the ticked ones.
  const owners = [
    ...(me ? [me] : []),
    ...selection.filter((o) => o !== me),
    ...(data?.owners ?? []).filter((o) => o !== me && !selection.includes(o)),
  ].filter((owner, index, all) => all.indexOf(owner) === index);

  const needle = query.trim().toLowerCase();
  const matches = needle
    ? owners.filter(
        (owner) => owner.includes(needle) || displayName(owner).toLowerCase().includes(needle),
      )
    : owners;

  // Ticking everybody is the same filter as ticking nobody, so the two render alike.
  const shown = selection.length === 0 ? owners : selection;
  const isNarrowed = selection.length > 0 && selection.length < owners.length;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Owner: ${triggerLabel(selection)}`}
          // Fixed width so ticking an owner never shifts the search bar beside it.
          className={cn(
            'flex h-14 w-28 items-center gap-1 rounded-[10px] border bg-background px-2 text-sm shadow-modal transition-colors',
            isNarrowed ? MAP_PILL_NARROWED_CLASS : 'border-border hover:bg-accent',
          )}
        >
          <UserRound className="size-[17px] flex-none text-muted-foreground" aria-hidden="true" />
          <span className="flex min-w-0 flex-1 items-center justify-center">
            {shown.length === 0 ? (
              <span className="font-medium">All owners</span>
            ) : (
              <span className="flex -space-x-2" aria-hidden="true">
                {shown.slice(0, MAX_TRIGGER_AVATARS).map((owner) => (
                  <span key={owner} className={cn(avatarClasses(owner), 'ring-2 ring-background')}>
                    {initials(owner)}
                  </span>
                ))}
                {shown.length > MAX_TRIGGER_AVATARS && (
                  <span
                    className={cn(
                      AVATAR_SHAPE,
                      'bg-muted text-muted-foreground ring-2 ring-background tabular-nums',
                    )}
                  >
                    +{shown.length - MAX_TRIGGER_AVATARS}
                  </span>
                )}
              </span>
            )}
          </span>
          <ChevronDown
            className={cn(
              'size-3 flex-none text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-64 rounded-[10px] border-border p-1.5 shadow-modal"
      >
        <div className="flex items-center justify-between px-2.5 pt-1.5 pb-2">
          <span className="text-xs font-bold text-primary-700">
            Filter zones &amp; deals by owner
          </span>
          {selection.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="px-1 text-xs font-semibold text-brand-800 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
        <div className="relative mx-2.5 mb-2">
          <Search
            className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search owners"
            placeholder="Search owners"
            className="h-8 w-full rounded-md border border-input bg-background pr-2 pl-7 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        {owners.length === 0 ? (
          <p className="px-2.5 py-1.5 text-sm text-muted-foreground">No owner yet</p>
        ) : matches.length === 0 ? (
          <p className="px-2.5 py-1.5 text-sm text-muted-foreground">No match</p>
        ) : (
          <ul className={cn('flex max-h-80 flex-col overflow-y-auto', THIN_SCROLLBAR)}>
            {matches.map((owner) => (
              <li key={owner}>
                <button
                  type="button"
                  aria-label={owner}
                  aria-pressed={selection.includes(owner)}
                  onClick={() => onToggle(owner)}
                  className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-accent"
                >
                  <span
                    className={cn(
                      'flex size-[17px] flex-none items-center justify-center rounded border-[1.6px] transition-colors',
                      selection.includes(owner)
                        ? 'border-primary-700 bg-primary-700 text-white'
                        : 'border-muted-progress bg-background',
                    )}
                  >
                    {selection.includes(owner) && (
                      <Check className="size-3" strokeWidth={3} aria-hidden="true" />
                    )}
                  </span>
                  <span aria-hidden="true" className={avatarClasses(owner)}>
                    {initials(owner)}
                  </span>
                  <span className="truncate text-sm text-primary-700">{displayName(owner)}</span>
                  {owner === me && (
                    <span className="flex-none text-[11px] font-medium text-muted-foreground">
                      (Me)
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

function triggerLabel(selection: OwnerSelection): string {
  if (selection.length === 0) return 'All owners';
  if (selection.length === 1) return displayName(selection[0]);
  return `${selection.length} owners`;
}

/** No user directory exists, so the name is read off the address; `a.leullier@` → "A. Leullier". */
function displayName(email: string): string {
  const parts = nameParts(email);
  if (parts.length === 0) return email;
  return parts
    .map((part) =>
      part.length === 1 ? `${part.toUpperCase()}.` : part[0].toUpperCase() + part.slice(1),
    )
    .join(' ');
}

function initials(email: string): string {
  const parts = nameParts(email);
  if (parts.length === 0) return email.slice(0, 2).toUpperCase();
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

function nameParts(email: string): string[] {
  return email
    .split('@')[0]
    .split(/[._-]+/)
    .filter((part) => /^[a-z]/i.test(part));
}

function avatarClasses(email: string): string {
  let hash = 0;
  for (const char of email) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return cn(AVATAR_SHAPE, 'text-white', AVATAR_COLOURS[hash % AVATAR_COLOURS.length]);
}
